<template>
  <div class="space-y-4">
    <!-- Search & Filters -->
    <div class="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-3">
      <div class="flex flex-1 flex-wrap items-center gap-3 w-full sm:w-auto">
        <div class="relative flex-1 min-w-[220px] max-w-md">
          <i class="fa fa-search absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
          <input
            v-model="localSearch"
            type="text"
            placeholder="Buscar por nombre, cédula o cargo..."
            class="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:bg-white focus:ring-orange-500 focus:border-orange-500"
            @input="onFilterChange"
          />
        </div>

        <select
          v-model="localStatus"
          class="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:bg-white focus:ring-orange-500 focus:border-orange-500"
          @change="onFilterChange"
        >
          <option value="all">Todos los estados</option>
          <option value="active">Solo Activos</option>
          <option value="inactive">Inactivos</option>
        </select>

        <select
          v-if="locations && locations.length > 0"
          v-model="localLocation"
          class="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:bg-white focus:ring-orange-500 focus:border-orange-500"
          @change="onFilterChange"
        >
          <option value="all">Todas las ubicaciones</option>
          <option v-for="loc in locations" :key="loc" :value="loc">{{ loc }}</option>
        </select>

        <select
          v-if="events && events.length > 0"
          v-model="localEvent"
          class="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:bg-white focus:ring-orange-500 focus:border-orange-500"
          @change="onFilterChange"
        >
          <option value="all">Todos los proyectos / eventos</option>
          <option v-for="ev in events" :key="ev" :value="ev">{{ ev }}</option>
        </select>
      </div>

      <button
        type="button"
        class="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-medium flex items-center gap-2 shadow-sm transition-all shrink-0 w-full sm:w-auto justify-center cursor-pointer active:scale-95"
        @click="$emit('open-employee-modal')"
      >
        <i class="fa fa-plus" />
        <span>Nuevo Personal</span>
      </button>
    </div>

    <!-- Employees Table -->
    <div class="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full text-left border-collapse text-sm">
          <thead>
            <tr class="bg-gray-50/70 border-b border-gray-200/80 text-[11px] uppercase tracking-wider text-gray-500 font-bold">
              <th class="py-3 px-4">Personal</th>
              <th class="py-3 px-4">Cargo / Función</th>
              <th class="py-3 px-4">Modalidad & Tarifa</th>
              <th class="py-3 px-4">Datos Bancarios</th>
              <th class="py-3 px-4">Estado</th>
              <th class="py-3 px-4 text-center">Turnos Pendientes</th>
              <th class="py-3 px-4 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr v-if="!employees?.data || employees.data.length === 0">
              <td colspan="7" class="py-12 text-center text-gray-400">
                <i class="fa fa-user-slash text-3xl mb-2 text-gray-300" />
                <p class="font-medium">No se encontraron empleados registrados.</p>
              </td>
            </tr>
            <tr 
              v-for="emp in employees?.data || []" 
              :key="emp.id"
              class="hover:bg-orange-50/20 transition-colors"
            >
              <!-- Name & ID -->
              <td class="py-3 px-4">
                <div class="flex items-center gap-3">
                  <div class="w-9 h-9 rounded-xl bg-gray-100 text-gray-700 border border-gray-200 font-bold text-xs flex items-center justify-center shrink-0">
                    {{ getInitials(emp.name) }}
                  </div>
                  <div>
                    <div class="font-bold text-gray-900 flex items-center gap-2 flex-wrap">
                      <span>{{ emp.name }}</span>
                      <span v-if="emp.location" class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 text-[10px] font-semibold border border-blue-200">
                        <i class="fa fa-map-marker-alt text-[9px]" />
                        {{ emp.location }}
                      </span>
                      <span v-if="emp.event_name" class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-purple-50 text-purple-700 text-[10px] font-semibold border border-purple-200" title="Evento / Proyecto asignado">
                        <i class="fa fa-folder text-[9px]" />
                        {{ emp.event_name }}
                      </span>
                    </div>
                    <div class="text-xs text-gray-500">
                      {{ emp.identification ? 'ID: ' + emp.identification : (emp.phone || 'Sin documento') }}
                    </div>
                  </div>
                </div>
              </td>

              <!-- Role -->
              <td class="py-3 px-4">
                <span class="px-2.5 py-1 rounded-lg text-xs font-semibold bg-gray-100 text-gray-800">
                  {{ emp.role_or_job || 'General' }}
                </span>
              </td>

              <!-- Salary & Rate -->
              <td class="py-3 px-4">
                <div class="font-bold text-gray-900">{{ formatUSD(emp.rate) }}</div>
                <div class="text-xs text-gray-400 capitalize">
                  {{ translateSalaryType(emp.type_salary) }}
                </div>
              </td>

              <!-- Bank Info -->
              <td class="py-3 px-4 max-w-xs">
                <div v-if="emp.bank_name || emp.account_number || emp.payment_identification" class="text-xs space-y-0.5">
                  <div class="font-semibold text-gray-800 flex items-center gap-1.5">
                    <i class="fa fa-university text-gray-400 text-[10px]" />
                    <span>{{ emp.bank_name || 'Banco' }}</span>
                    <span v-if="emp.account_type" class="text-gray-400 text-[10px]">({{ emp.account_type }})</span>
                  </div>
                  <div v-if="emp.account_number" class="font-mono text-gray-600 text-[11px] truncate flex items-center gap-1">
                    <span>{{ emp.account_number }}</span>
                    <button 
                      type="button"
                      class="text-gray-400 hover:text-orange-500 text-[10px] cursor-pointer" 
                      title="Copiar cuenta"
                      @click="$emit('copy-to-clipboard', emp.account_number, 'Cuenta copiada')"
                    >
                      <i class="fa fa-copy" />
                    </button>
                  </div>
                  <div v-if="emp.payment_identification" class="text-amber-700 text-[11px] font-medium flex items-center gap-1">
                    <span>P. Móvil: {{ emp.payment_identification }}</span>
                    <button 
                      type="button"
                      class="text-amber-600 hover:text-orange-500 text-[10px] cursor-pointer" 
                      title="Copiar Pago Móvil"
                      @click="$emit('copy-to-clipboard', emp.payment_identification, 'Pago Móvil copiado')"
                    >
                      <i class="fa fa-copy" />
                    </button>
                  </div>
                </div>
                <span v-else class="text-xs text-gray-400 italic">No registrada</span>
              </td>

              <!-- Status -->
              <td class="py-3 px-4">
                <span 
                  :class="emp.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-gray-100 text-gray-600 border-gray-200'"
                  class="px-2 py-0.5 rounded-full text-xs font-bold border"
                >
                  {{ emp.status === 'active' ? 'Activo' : 'Inactivo' }}
                </span>
              </td>

              <!-- Pending Shifts -->
              <td class="py-3 px-4 text-center">
                <div v-if="emp.pending_shifts_count > 0">
                  <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-800">
                    {{ emp.pending_shifts_count }} turnos
                  </span>
                  <div class="text-[11px] font-bold text-gray-800 mt-0.5">
                    {{ formatUSD(emp.pending_shifts_sum || 0) }}
                  </div>
                </div>
                <span v-else class="text-xs text-gray-400">-</span>
              </td>

              <!-- Actions -->
              <td class="py-3 px-4 text-right">
                <div class="flex items-center justify-end gap-1.5">
                  <button
                    v-if="emp.status === 'active'"
                    type="button"
                    class="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer"
                    title="Registrar Turno a este empleado"
                    @click="$emit('open-single-shift-modal', emp)"
                  >
                    <i class="fa fa-calendar-plus text-sm" />
                  </button>

                  <button
                    v-if="emp.pending_shifts_count > 0"
                    type="button"
                    class="p-1.5 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors cursor-pointer"
                    title="Liquidar turnos pendientes"
                    @click="$emit('open-settlement-modal', emp)"
                  >
                    <i class="fa fa-hand-holding-usd text-sm" />
                  </button>

                  <button
                    type="button"
                    class="p-1.5 text-gray-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors relative cursor-pointer"
                    title="Expediente / Documentos (RIF, Certificados, etc.)"
                    @click="$emit('open-documents-modal', emp)"
                  >
                    <i class="fa fa-folder-open text-sm" />
                    <span 
                      v-if="emp.documents_count > 0" 
                      class="absolute -top-1 -right-1 px-1 min-w-[14px] h-[14px] rounded-full text-[9px] font-bold bg-amber-500 text-white flex items-center justify-center leading-none"
                    >
                      {{ emp.documents_count }}
                    </span>
                  </button>

                  <button
                    type="button"
                    class="p-1.5 text-gray-500 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors cursor-pointer"
                    title="Editar datos"
                    @click="$emit('open-employee-modal', emp)"
                  >
                    <i class="fa fa-edit text-sm" />
                  </button>

                  <button
                    type="button"
                    class="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                    title="Eliminar o Desactivar"
                    @click="$emit('delete-employee', emp)"
                  >
                    <i class="fa fa-trash-alt text-sm" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="employees?.links" class="p-4 border-t border-gray-100 flex justify-end">
        <Pagination :links="employees.links" :query-params="{ tab: 'employees', search: localSearch, status: localStatus, location: localLocation, event_name: localEvent }" />
      </div>
    </div>
  </div>
</template>

<script>
import Pagination from '@/Shared/Pagination'

export default {
  name: 'PaysheetEmployeesTab',

  components: {
    Pagination,
  },

  props: {
    employees: {
      type: Object,
      required: true,
    },
    locations: {
      type: Array,
      default: () => [],
    },
    events: {
      type: Array,
      default: () => [],
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
    currentExchangeRate: {
      type: Number,
      default: 1.0,
    },
  },

  emits: [
    'filter',
    'open-employee-modal',
    'open-single-shift-modal',
    'open-settlement-modal',
    'open-documents-modal',
    'delete-employee',
    'copy-to-clipboard',
  ],

  data() {
    return {
      localSearch: this.filters?.search || '',
      localStatus: this.filters?.status || 'all',
      localLocation: this.filters?.location || 'all',
      localEvent: this.filters?.event_name || 'all',
    }
  },

  methods: {
    onFilterChange() {
      this.$emit('filter', {
        search: this.localSearch,
        status: this.localStatus,
        location: this.localLocation,
        event_name: this.localEvent,
      })
    },

    formatUSD(val) {
      const num = parseFloat(val) || 0
      return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },

    translateSalaryType(type) {
      const map = {
        shift: 'Por Turno',
        piecework: 'A Destajo',
        hourly: 'Por Hora',
        fixed: 'Sueldo Fijo',
      }
      return map[type] || type || 'Turno'
    },

    getInitials(name) {
      if (!name) return 'RG'
      return name
        .split(' ')
        .map(n => n[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
    },
  },
}
</script>
